import { action, makeObservable, observable, runInAction } from 'mobx'

import { BicCompletionMode, SitePermitStatus } from '~/client/graph'

import EventsStore from '../../stores/EventStore/Events.store'
import PermitBallInCourtStore, {
  IBicRuleStatus,
} from '../../stores/ui/PermitBallInCourt.store'
import PermitLogActionBarStore from '../PermitLogActionBar.store'
import SitePermitCreationFormStore from './WorkflowCreationForm.store'

export default class PermitApprovalStore {
  /** The rules the user picks from when their sign-off could count toward more than one. */
  @observable public ruleChoices: IBicRuleStatus[] | null = null
  private resolveRuleChoice: ((ruleId: string | null) => void) | null = null

  public constructor(
    private readonly sitePermitFormStore: SitePermitCreationFormStore,
    private readonly actionBarStore: PermitLogActionBarStore,
    protected readonly closeDialog: () => void,
    private readonly eventsStore?: EventsStore,
    private readonly permitBicStore?: PermitBallInCourtStore,
  ) {
    makeObservable(this)
  }

  public advancePermitWithApproval = async (targetStatus: SitePermitStatus) => {
    const pendingRules = this.pendingRulesForUser
    let ruleId = pendingRules[0]?.ruleId

    if (pendingRules.length > 1) {
      ruleId = await this.askWhichRuleToSignFor(pendingRules)
      if (!ruleId) return
    }

    this.signOffAndAdvance(targetStatus, ruleId)
  }

  @action.bound
  public chooseRule(ruleId: string) {
    this.closeRuleChoice(ruleId)
  }

  @action.bound
  public cancelRuleChoice() {
    this.closeRuleChoice(null)
  }

  @action
  private signOffAndAdvance(targetStatus: SitePermitStatus, ruleId?: string) {
    const {
      existingPermit,
      template,
      editablePermit,
      workflowFieldsStore,
      isStepAdvancePending,
    } = this.sitePermitFormStore

    // The caller was cleared for existingPermit's step; while an earlier advance
    // is unsaved, this one would sign the step after it.
    if (isStepAdvancePending) {
      return
    }

    const { currentWorkflowStepId } = existingPermit

    if (!template) {
      this.proceedToNextStep(targetStatus)
      return
    }

    const completionMode = template.getStepCompletionMode(currentWorkflowStepId)
    const requireAllSubmissions = template.getStepRequireAllSubmissions(
      currentWorkflowStepId,
    )

    const isAdminOrFormsMaster =
      this.eventsStore?.appState?.userActiveProjectSettings
        ?.isAdminOrFormsMaster

    const stepRuleIds = template.getStepRuleIds(currentWorkflowStepId)
    const userId = this.eventsStore?.appState?.user?.id

    const isAdminInBicRules =
      isAdminOrFormsMaster &&
      userId &&
      this.permitBicStore &&
      stepRuleIds?.length > 0 &&
      this.permitBicStore.isUserInRequiredBicUsers(
        userId,
        editablePermit,
        stepRuleIds,
      )

    const shouldFollowBicFlow =
      requireAllSubmissions && (!isAdminOrFormsMaster || isAdminInBicRules)

    // Record submission for any user when requireAllSubmissions is enabled
    // This includes admins not in BIC list - their fields are still recorded
    if (requireAllSubmissions && userId) {
      const currentStepFieldIds = workflowFieldsStore.currentStepFieldIds
      const currentStepFields = editablePermit.fields.filter(f =>
        currentStepFieldIds.has(f.fieldId),
      )
      // With no rule left to sign for, the user updates the sign-off they already gave
      const submissionRuleId =
        completionMode === BicCompletionMode.OnePerRule
          ? (ruleId ??
            existingPermit.getUserSubmission(currentWorkflowStepId, userId)
              ?.ruleId)
          : undefined

      existingPermit.addSubmission(
        currentWorkflowStepId,
        userId,
        currentStepFields,
        submissionRuleId,
      )
      editablePermit.addSubmission(
        currentWorkflowStepId,
        userId,
        currentStepFields,
        submissionRuleId,
      )
    }

    if (shouldFollowBicFlow) {
      if (this.permitBicStore) {
        if (!stepRuleIds?.length) {
          this.proceedToNextStep(targetStatus)
          return
        }

        const canComplete = this.permitBicStore.canStepBeCompleted(
          editablePermit,
          currentWorkflowStepId,
          stepRuleIds,
          completionMode,
        )

        if (!canComplete) {
          this.savePermitWithoutStatusChange()
          return
        }
      } else {
        this.savePermitWithoutStatusChange()
        return
      }
    }

    this.proceedToNextStep(targetStatus)
  }

  // In one-sign-off-per-rule mode a sign-off counts toward one of the rules still waiting on
  // the user. Empty in the other modes.
  private get pendingRulesForUser(): IBicRuleStatus[] {
    const { existingPermit, template, editablePermit } =
      this.sitePermitFormStore
    const { currentWorkflowStepId } = existingPermit
    const userId = this.eventsStore?.appState?.user?.id

    if (
      !template ||
      !userId ||
      !this.permitBicStore ||
      template.getStepCompletionMode(currentWorkflowStepId) !==
        BicCompletionMode.OnePerRule
    ) {
      return []
    }

    return this.permitBicStore.getPendingRulesForUser(
      userId,
      editablePermit,
      template.getStepRuleIds(currentWorkflowStepId),
    )
  }

  // Resolves the rule the user picked, or null when they went back.
  private askWhichRuleToSignFor(
    rules: IBicRuleStatus[],
  ): Promise<string | null> {
    return new Promise(resolve => {
      runInAction(() => {
        this.resolveRuleChoice = resolve
        this.ruleChoices = rules
      })
    })
  }

  private closeRuleChoice(ruleId: string | null) {
    const resolve = this.resolveRuleChoice
    this.resolveRuleChoice = null
    this.ruleChoices = null
    resolve?.(ruleId)
  }

  private proceedToNextStep(targetStatus: SitePermitStatus) {
    const finalStatus = this.sitePermitFormStore.isLastStep
      ? SitePermitStatus.Done
      : targetStatus

    this.updatePermit(
      finalStatus,
      finalStatus === SitePermitStatus.Done ? this.closeDialog : undefined,
    )
  }

  public approvePermit = async () => {
    if (
      !(await this.sitePermitFormStore.askToConfirmHoursWarnings('approve'))
    ) {
      return
    }
    await this.advancePermitWithApproval(SitePermitStatus.Accepted)
  }

  private savePermitWithoutStatusChange = () => {
    const { isPermitValid, editablePermit, updatePermit } =
      this.sitePermitFormStore

    if (isPermitValid) {
      const message = editablePermit.isChanged
        ? this.actionBarStore?.draftMessage
        : null
      updatePermit(message, undefined, true)
    }
  }

  @action
  public updatePermit = (
    status: SitePermitStatus,
    closeDialog?: () => void,
  ) => {
    const { isPermitValid, editablePermit, updatePermit } =
      this.sitePermitFormStore

    if (isPermitValid) {
      editablePermit.setStatus(status)
      const message = editablePermit.isChanged
        ? this.actionBarStore.draftMessage
        : null
      updatePermit(message, closeDialog)
    }
  }
}
